<div id="overlay">
  <div class="overlay-text">
    <mat-spinner></mat-spinner>
    <h1>Calculating...</h1>
  </div>
</div>

@if (isLoaded) {
  <div class="overview-page">
    <div class="page-head">
      <div class="page-head-main">
        <div class="page-head-left">
          <h1 class="page-title">
            @if (portfolio?.name) {
              <span class="pf-title-name">{{ portfolio.name }}</span>
            }
            @if (portfolio?.name) {
              <span class="pf-title-sep">·</span>
            }
            Backtest
          </h1>
          <div class="page-sub">
            Benchmark:
            {{
              selectedBenchmark?.index_name_short || portfolio?.benchmark || "—"
            }}
            · {{ generalData.length }} assets
          </div>
        </div>
        @if (portfolio) {
          <div class="exp-settings-strip">
            <div class="exp-field">
              <label>Start date</label>
              <input
                type="date"
                class="exp-input"
                [(ngModel)]="editStart"
                [disabled]="isApplying"
              />
            </div>
            <div class="exp-field">
              <label>End date</label>
              <input
                type="date"
                class="exp-input"
                [min]="editStart"
                [(ngModel)]="editEnd"
                [disabled]="isApplying"
              />
            </div>
            <div class="exp-field exp-field-grow">
              <label>Benchmark</label>
              <select
                class="exp-input exp-select"
                [(ngModel)]="editBenchmarkName"
                [disabled]="isApplying"
              >
                <option value="" disabled>Select benchmark</option>
                @for (g of benchmarkGroups; track g.label) {
                  <optgroup [label]="g.label">
                    @for (b of g.items; track b.name) {
                      <option [value]="b.name">{{ b.full }}</option>
                    }
                  </optgroup>
                }
                @if (editBenchmarkName && !benchmarkGroups.length) {
                  <option [value]="editBenchmarkName">
                    {{ editBenchmarkName }}
                  </option>
                }
              </select>
            </div>
            <div class="exp-settings-actions">
              <button
                class="btn btn-text btn-sm"
                type="button"
                (click)="onResetSettings()"
                [disabled]="isApplying || !hasSettingsChanges"
              >
                Reset
              </button>
              <button
                class="btn btn-primary btn-sm"
                type="button"
                (click)="onApply()"
                [disabled]="!canApply"
              >
                @if (!isApplying) {
                  Apply
                }
                @if (isApplying) {
                  Applying…
                }
              </button>
            </div>
          </div>
        }
      </div>
      <div class="d-flex align-items-end" style="gap: 8px">
        <button
          class="btn btn-ghost"
          type="button"
          (click)="onExportPdf()"
          [disabled]="!portfolio || isApplying"
        >
          Export
        </button>
      </div>
    </div>
    <!-- Row 1: KPI tiles -->
    <div class="row g-3">
      <div class="col-md-3 col-sm-6">
        <section class="kpi h-100">
          <span class="label">Total return</span>
          <span
            class="value"
            [class.pos]="(kpis.totalReturn ?? 0) > 0"
            [class.neg]="(kpis.totalReturn ?? 0) < 0"
          >
            {{ fmtDeltaPct(kpis.totalReturn, 2) }}
          </span>
          <span
            class="delta"
            [class.pos]="(kpis.totalReturn ?? 0) > (benchKpis.totalReturn ?? 0)"
            [class.neg]="
              (kpis.totalReturn ?? 0) <= (benchKpis.totalReturn ?? 0)
            "
          >
            @if (benchKpis.totalReturn !== null) {
              {{
                (kpis.totalReturn ?? 0) > (benchKpis.totalReturn ?? 0)
                  ? "▲"
                  : "▼"
              }}
              vs {{ selectedBenchmark?.index_name_short || "Bench" }}
              {{ fmtDeltaPct(benchKpis.totalReturn, 1) }}
            }
            @if (benchKpis.totalReturn === null) {
              Since inception
            }
          </span>
          <app-sparkline
            class="spark"
            [data]="sparks.total"
            [width]="90"
            [height]="24"
          ></app-sparkline>
        </section>
      </div>
      <div class="col-md-3 col-sm-6">
        <section class="kpi h-100">
          <span class="label">Annualized return</span>
          <span class="value">{{ fmtPct(kpis.annualizedReturn, 2) }}</span>
          <span class="delta">CAGR 5Yr: {{ fmtPct(kpis.cagrY5, 2) }}</span>
          <app-sparkline
            class="spark"
            [data]="sparks.annual"
            [width]="90"
            [height]="24"
          ></app-sparkline>
        </section>
      </div>
      <div class="col-md-3 col-sm-6">
        <section class="kpi h-100">
          <span class="label">Sharpe ratio</span>
          <span class="value">{{ fmtNum(kpis.sharpe) }}</span>
          <span
            class="delta"
            [class.pos]="(kpis.sharpe ?? 0) > (benchKpis.sharpe ?? 0)"
            [class.neg]="(kpis.sharpe ?? 0) <= (benchKpis.sharpe ?? 0)"
          >
            @if (benchKpis.sharpe !== null) {
              {{ (kpis.sharpe ?? 0) > (benchKpis.sharpe ?? 0) ? "▲" : "▼" }}
              vs {{ selectedBenchmark?.index_name_short || "Bench" }}
              {{ fmtNum(benchKpis.sharpe) }}
            }
            @if (benchKpis.sharpe === null) {
              Risk-adjusted
            }
          </span>
          <app-sparkline
            class="spark"
            [data]="sparks.sharpe"
            [width]="90"
            [height]="24"
            color="#00E5A0"
          ></app-sparkline>
        </section>
      </div>
      <div class="col-md-3 col-sm-6">
        <section class="kpi h-100">
          <span class="label">Max drawdown</span>
          <span class="value neg">{{ fmtPct(kpis.maxDrawdown, 2) }}</span>
          <span class="delta neg">
            @if (benchKpis.maxDrawdown !== null) {
              ▼ vs {{ selectedBenchmark?.index_name_short || "Bench" }}
              {{ fmtPct(benchKpis.maxDrawdown, 1) }}
            }
            @if (benchKpis.maxDrawdown === null) {
              Peak-to-trough
            }
          </span>
          <app-sparkline
            class="spark"
            [data]="sparks.drawdown"
            [width]="90"
            [height]="24"
            color="#FF5C7A"
          ></app-sparkline>
        </section>
      </div>
    </div>
    <!-- Row 2: Cumulative chart + Valuation/Risk stack -->
    <div class="row g-3 mt-0">
      <div class="col-lg-8" id="overview-cumulative">
        @if (plotData?.cumulative_returns?.Portfolio?.length) {
          <app-cummulative-return-chart
            [loaded]="chartsReady"
            [subHeader]="portfolio.name"
            [plotData]="plotData"
            [benchmark]="benchmark"
            [startDate]="chartGlobalStartDate"
            [endDate]="chartGlobalEndDate"
          ></app-cummulative-return-chart>
        } @else {
          <div
            class="card cumulative-return-chart h-100"
            style="display: flex; flex-direction: column"
          >
            <div
              class="card-body"
              style="
                display: flex;
                flex-direction: column;
                flex: 1;
                min-height: 0;
              "
            >
              <div class="card-head">
                <div class="card-title">Cumulative return</div>
              </div>
              <app-chart-placeholder [loaded]="false"></app-chart-placeholder>
            </div>
          </div>
        }
      </div>
      <div class="col-lg-4">
        <div class="d-flex flex-column" style="gap: 14px; height: 100%">
          <div class="card flex-grow-1">
            <div class="card-body">
              <div class="card-head">
                <div class="card-title">Valuation</div>
              </div>
              <div class="kv">
                <span class="k">Dividend yield</span>
                <span class="v">{{
                  valuation.dividendYield !== null
                    ? fmtNum(valuation.dividendYield, 2) + "%"
                    : "—"
                }}</span>
                <span class="k">P/E</span>
                <span class="v">{{ fmtNum(valuation.pe, 2) }}</span>
                <span class="k">P/S</span>
                <span class="v">{{ fmtNum(valuation.ps, 2) }}</span>
                <span class="k">P/B</span>
                <span class="v">{{ fmtNum(valuation.pb, 2) }}</span>
                <span class="k">Avg. Market Cap</span>
                <span class="v">{{ fmtMoney(valuation.avgMarketCap) }}</span>
                <span class="k">Avg. Enterprise Value</span>
                <span class="v">{{
                  fmtMoney(valuation.avgEnterpriseValue)
                }}</span>
                <span class="k">Avg. EBITDA</span>
                <span class="v">{{ fmtMoney(valuation.avgEBITDA) }}</span>
              </div>
            </div>
          </div>
          <div class="card flex-grow-1">
            <div class="card-body">
              <div class="card-head">
                <div class="card-title">Risk snapshot</div>
              </div>
              <div class="risk-table">
                <div class="risk-row risk-header">
                  <span>Metric</span>
                  <span>Portfolio</span>
                  <span>{{ selectedBenchmark?.index_name_short || 'Benchmark' }}</span>
                </div>
                <div class="risk-row">
                  <span>Volatility</span>
                  <span>{{ fmtPct(risk.volatility, 2) }}</span>
                  <span>{{ riskBench.volatility !== null ? fmtPct(riskBench.volatility, 2) : '—' }}</span>
                </div>
                <div class="risk-row">
                  <span>VaR 95</span>
                  <span>{{ fmtPct(risk.var95, 2) }}</span>
                  <span>{{ riskBench.var95 !== null ? fmtPct(riskBench.var95, 2) : '—' }}</span>
                </div>
                <div class="risk-row">
                  <span>VaR 99</span>
                  <span>{{ fmtPct(risk.var99, 2) }}</span>
                  <span>{{ riskBench.var99 !== null ? fmtPct(riskBench.var99, 2) : '—' }}</span>
                </div>
                <div class="risk-row">
                  <span>Alpha</span>
                  <span [class.pos]="(risk.alpha ?? 0) >= 0" [class.neg]="(risk.alpha ?? 0) < 0">{{ fmtPct(risk.alpha, 2) }}</span>
                  <span>—</span>
                </div>
                <div class="risk-row">
                  <span>Beta</span>
                  <span>{{ fmtNum(risk.beta) }}</span>
                  <span>—</span>
                </div>
                <div class="risk-row">
                  <span>Treynor Ratio</span>
                  <span>{{ fmtNum(risk.treynor) }}</span>
                  <span>—</span>
                </div>
                <div class="risk-row">
                  <span>Calmar Ratio</span>
                  <span>{{ fmtNum(risk.calmar) }}</span>
                  <span>{{ riskBench.calmar !== null ? fmtNum(riskBench.calmar) : '—' }}</span>
                </div>
                <div class="risk-row">
                  <span>Tracking error</span>
                  <span>{{ fmtPct(risk.trackingError, 2) }}</span>
                  <span>—</span>
                </div>
                <div class="risk-row">
                  <span>Information ratio</span>
                  <span>{{ fmtNum(risk.informationRatio) }}</span>
                  <span>—</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Row 3: Annual performance + Weights vs Volatility -->
    <div class="row g-3 mt-0">
      <div class="col-lg-6" id="overview-annual">
        <div class="card h-100">
          <div class="card-body">
            <div class="card-head">
              <div class="card-title">Annual performance</div>
              <div class="card-tools">
                <div class="seg">
                  <button
                    type="button"
                    [class.active]="annualChartType === 'annualized_return'"
                    (click)="setAnnualMetric('annualized_return')"
                  >
                    Return
                  </button>
                  <button
                    type="button"
                    [class.active]="annualChartType === 'annualized_sharpe'"
                    (click)="setAnnualMetric('annualized_sharpe')"
                  >
                    Sharpe
                  </button>
                  <button
                    type="button"
                    [class.active]="
                      annualChartType === 'annualized_std_deviation'
                    "
                    (click)="setAnnualMetric('annualized_std_deviation')"
                  >
                    Volatility
                  </button>
                </div>
              </div>
            </div>
            @if (hasAnnualData && sharpeChartData.length > 0) {
              <app-line-chart
                [loaded]="chartsReady"
                [series]="sharpeChartData"
                [extraOptions]="annualChartOptions"
                [benchmark]="benchmark"
                [startDate]="chartGlobalStartDate"
                [endDate]="chartGlobalEndDate"
              ></app-line-chart>
            } @else {
              <div class="empty-cell">
                No annual performance data available.
              </div>
            }
          </div>
        </div>
      </div>
      <div class="col-lg-6" id="overview-weights">
        <div class="card h-100">
          <div class="card-body">
            <div class="card-head">
              <div class="card-title">Weights vs Volatility vs Return per asset</div>
              <div class="card-tools"></div>
            </div>
            @if (weightsRows.length) {
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="overview-hbar-label">Portfolio weight</div>
                  <app-hbar-list
                    [rows]="weightsRows"
                    [format]="fmtPct.bind(this)"
                  ></app-hbar-list>
                </div>
                <div class="col-md-4">
                  <div class="overview-hbar-label">Annualized volatility</div>
                  <app-hbar-list
                    [rows]="volRows"
                    [format]="fmtPct.bind(this)"
                    [showLabel]="false"
                    colorMode="neg"
                  ></app-hbar-list>
                </div>
                <div class="col-md-4">
                    <div class="overview-hbar-label">Annualized return</div>
                    <app-hbar-list
                      [rows]="returnRows"
                      [format]="fmtPct.bind(this)"
                      [showLabel]="false"
                      colorMode="pos"
                    ></app-hbar-list>
                  </div>
              </div>
            } @else {
              <div class="empty-cell">No constituent weights available.</div>
            }
          </div>
        </div>
      </div>
    </div>
    <!-- Row 4: Constituents table -->
    @if (generalData.length > 0) {
      <div class="row g-3 mt-0">
        <div class="col-12" id="overview-constituents">
          <div class="card">
            <div class="card-body">
              <div class="card-head">
                <div class="card-title">Portfolio constituents</div>
                <div class="card-tools"></div>
              </div>
              <app-cim-datatable
                [columns]="portfolioConstituentsTableColumns"
                [data]="generalData"
                sortProp="Weight"
                sortDirection="desc"
                [needPaginator]="false"
              >
              </app-cim-datatable>
            </div>
          </div>
        </div>
      </div>
    }
    <!-- Row 5: Risk contribution + Marginal risk -->
    <div class="row g-3 mt-0" id="overview-risk-contrib">
      <div class="col-lg-6">
        @if (riskContributionData?.length) {
          <app-risk-contribution-column-chart
            style="display: block; height: 100%"
            [riskContributionRaw]="riskContributionData"
            [subHeader]="{ portfolio: portfolio.name }"
          >
          </app-risk-contribution-column-chart>
        }
      </div>
      <div class="col-lg-6">
        @if (marginalRiskContributionData?.[0]?.data?.length) {
          <app-portfolio-return-bar-chart
            style="display: block; height: 100%"
            [title]="'Marginal risk'"
            [rawData]="marginalRiskContributionData"
            [subHeader]="{ portfolio: portfolio.name }"
          >
          </app-portfolio-return-bar-chart>
        }
      </div>
    </div>
  </div>
}

<!-- Loading skeleton -->
@if (!isLoaded) {
  <div class="overview-page">
    <div class="page-head">
      <div class="page-head-main">
        <h1 class="page-title">Backtest</h1>
        <div class="page-sub">Loading…</div>
      </div>
    </div>
    <div class="row g-3">
      <div class="col-md-3 col-sm-6">
        <div class="card shimmer" style="height: 112px"></div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="card shimmer" style="height: 112px"></div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="card shimmer" style="height: 112px"></div>
      </div>
      <div class="col-md-3 col-sm-6">
        <div class="card shimmer" style="height: 112px"></div>
      </div>
    </div>
    <div class="row g-3 mt-0">
      <div class="col-lg-8">
        <div class="card shimmer" style="height: 380px"></div>
      </div>
      <div class="col-lg-4">
        <div class="card shimmer" style="height: 380px"></div>
      </div>
    </div>
    <div class="row g-3 mt-0">
      <div class="col-12">
        <div class="card shimmer" style="height: 240px"></div>
      </div>
    </div>
  </div>
}
